import React, { useState, useEffect } from 'react';
import { Plus, DollarSign, RefreshCw, Truck } from 'lucide-react';

// Custom hooks
import { useFuelData } from './useFuelData';

// API functions
import { 
  saveTank, deleteTank, saveTransaction, deleteTransaction, 
  saveDelivery, updatePrice, fetchTrucks, saveTruck, 
  deleteTruck, refillTruck, createTruckTransaction
} from './api';

// Components
import TankCard from './TankCard';
import LowFuelAlert from './LowFuelAlert';
import TodayStats from './TodayStats';
import TransactionsTable from './TransactionsTable';
import TankModal from './TankModal';
import TransactionModal from './TransactionModal';
import DeliveryModal from './DeliveryModal';
import PriceModal from './PriceModal';
import TruckCard from './TruckCard';
import TruckModal from './TruckModal';
import TruckRefillModal from './TruckRefillModal';
import TruckTransactionsModal from './TruckTransactionsModal';

// Constants & Utils
import { EMPTY_TANK, EMPTY_TX, EMPTY_DELIVERY, EMPTY_PRICE, EMPTY_TRUCK } from './constants';
import { generateFuelInvoice } from '../FuelInvoicePDF';

const FuelView = () => {
  const {
    inventory,
    transactions,
    todayStats,
    aircraft,
    owners,
    staff,
    loading,
    error,
    fetchAll,
  } = useFuelData();

  const [trucks, setTrucks] = useState([]);
  const [activeTab, setActiveTab] = useState('tanks');
  const [saving, setSaving] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);

  // Modal states
  const [showTankModal, setShowTankModal] = useState(false);
  const [showTxModal, setShowTxModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showTruckModal, setShowTruckModal] = useState(false);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [showTruckTxModal, setShowTruckTxModal] = useState(false);
  const [editingTank, setEditingTank] = useState(null);
  const [editingTx, setEditingTx] = useState(null);
  const [editingTruck, setEditingTruck] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);

  // Form states
  const [tankForm, setTankForm] = useState(EMPTY_TANK);
  const [txForm, setTxForm] = useState(EMPTY_TX);
  const [deliveryForm, setDeliveryForm] = useState(EMPTY_DELIVERY);
  const [priceForm, setPriceForm] = useState(EMPTY_PRICE);
  const [truckForm, setTruckForm] = useState(EMPTY_TRUCK);

  // Load trucks
  const loadTrucks = async () => {
    try {
      const data = await fetchTrucks(true);
      setTrucks(data);
    } catch (err) {
      console.error('Failed to load trucks', err);
    }
  };

  useEffect(() => {
    loadTrucks();
  }, []);

  // ─── Tank handlers ──────────────────────────────────────────────
  const openCreateTank = () => {
    setEditingTank(null);
    setTankForm(EMPTY_TANK);
    setShowTankModal(true);
  };

  const openEditTank = (inv) => {
    setEditingTank(inv);
    setTankForm({
      fuel_type: inv.fuel_type,
      tank_name: inv.tank_name ?? '',
      capacity_gallons: inv.capacity_gallons ?? '',
      current_volume_gallons: inv.current_volume_gallons ?? '',
      reorder_level_gallons: inv.reorder_level_gallons ?? '',
      price_per_gallon: inv.price_per_gallon ?? '',
    });
    setShowTankModal(true);
  };

  const handleTankSave = async () => {
    if (!tankForm.current_volume_gallons || !tankForm.price_per_gallon) {
      alert('Current volume and price are required.');
      return;
    }
    setSaving(true);
    try {
      await saveTank(tankForm, editingTank);
      await fetchAll();
      setShowTankModal(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving tank');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTank = async (inv) => {
    if (!window.confirm(`Delete tank "${inv.tank_name || inv.fuel_type}"? This cannot be undone.`)) return;
    try {
      await deleteTank(inv.id);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting tank');
    }
  };

  // ─── Transaction handlers ───────────────────────────────────────
  const openCreateTx = () => {
    setEditingTx(null);
    const firstTank = inventory[0];
    setTxForm({
      ...EMPTY_TX,
      fuel_type: firstTank?.fuel_type || 'JetA',
      price_per_gallon: firstTank?.price_per_gallon ? String(firstTank.price_per_gallon) : '',
      truck_id: '',
    });
    setShowTxModal(true);
  };

  const openEditTx = (tx) => {
    setEditingTx(tx);
    setTxForm({
      aircraft_id: tx.aircraft_id || '',
      owner_id: tx.owner_id || '',
      fuel_type: tx.fuel_type || 'JetA',
      gallons: tx.gallons != null ? String(tx.gallons) : '',
      price_per_gallon: tx.price_per_gallon != null ? String(tx.price_per_gallon) : '',
      staff_id: tx.staff_id || '',
      truck_id: '',
    });
    setShowTxModal(true);
  };

  const closeTxModal = () => {
    setShowTxModal(false);
    setEditingTx(null);
    setTxForm(EMPTY_TX);
  };

  const handleTransactionSave = async () => {
    if (!txForm.aircraft_id || !txForm.gallons || !txForm.price_per_gallon) {
      alert('Aircraft, volume (gallons) and price are required.');
      return;
    }
    setSaving(true);
    try {
      // Si un truck est sélectionné, utiliser la route truck
      if (txForm.truck_id) {
        await createTruckTransaction(txForm.truck_id, txForm);
      } else {
        await saveTransaction(txForm, editingTx);
      }
      await fetchAll();
      await loadTrucks(); // Recharger les trucks pour mettre à jour les volumes
      closeTxModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTx = async (tx) => {
    const label = tx.transaction_number || tx.id?.slice(0, 8);
    if (!window.confirm(`Delete transaction ${label}?\nThe gallons will be returned to the tank.`)) return;
    try {
      await deleteTransaction(tx.id);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting transaction');
    }
  };

  // ─── Delivery handler ───────────────────────────────────────────
  const handleDelivery = async () => {
    if (!deliveryForm.gallons || !deliveryForm.price_per_gallon) {
      alert('Volume (gallons) and price are required.');
      return;
    }
    setSaving(true);
    try {
      await saveDelivery(deliveryForm);
      await fetchAll();
      setShowDeliveryModal(false);
      setDeliveryForm(EMPTY_DELIVERY);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving delivery');
    } finally {
      setSaving(false);
    }
  };

  // ─── Price handler ──────────────────────────────────────────────
  const handlePriceUpdate = async () => {
    if (!priceForm.price_per_gallon) return;
    setSaving(true);
    try {
      await updatePrice(priceForm.fuel_type, priceForm.price_per_gallon);
      await fetchAll();
      setShowPriceModal(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating price');
    } finally {
      setSaving(false);
    }
  };

  // ─── Invoice handlers ───────────────────────────────────────────
  const handleViewInvoice = async (tx) => {
    setGeneratingInvoice(tx.id);
    try {
      await generateFuelInvoice(tx.id, 'preview');
    } catch (err) {
      alert('Error generating invoice: ' + err.message);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const handleDownloadInvoice = async (tx) => {
    setGeneratingInvoice(tx.id);
    try {
      await generateFuelInvoice(tx.id, 'download');
    } catch (err) {
      alert('Error generating invoice: ' + err.message);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  // ─── Truck handlers ─────────────────────────────────────────────
  const openCreateTruck = () => {
    setEditingTruck(null);
    setTruckForm(EMPTY_TRUCK);
    setShowTruckModal(true);
  };

  const openEditTruck = (truck) => {
    setEditingTruck(truck);
    setTruckForm({
      truck_number: truck.truck_number,
      truck_name: truck.truck_name || '',
      fuel_type: truck.fuel_type,
      capacity_gallons: truck.capacity_gallons,
      current_volume_gallons: truck.current_volume_gallons,
      license_plate: truck.license_plate || '',
      driver_name: truck.driver_name || '',
      driver_phone: truck.driver_phone || '',
      status: truck.status || 'available',
      last_maintenance_date: truck.last_maintenance_date || '',
      next_maintenance_date: truck.next_maintenance_date || '',
      notes: truck.notes || '',
      is_active: truck.is_active !== false,
    });
    setShowTruckModal(true);
  };

  const handleTruckSave = async () => {
    if (!truckForm.truck_number || !truckForm.fuel_type || !truckForm.capacity_gallons) {
      alert('Truck number, fuel type, and capacity are required.');
      return;
    }
    setSaving(true);
    try {
      await saveTruck(truckForm, editingTruck);
      await loadTrucks();
      setShowTruckModal(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving truck');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTruck = async (truck) => {
    if (!window.confirm(`Delete truck "${truck.truck_number}"? This will deactivate it from the system.`)) return;
    try {
      await deleteTruck(truck.id, false);
      await loadTrucks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting truck');
    }
  };

  const handleRefillTruck = async (refillData) => {
    if (!selectedTruck) return;
    setSaving(true);
    try {
      await refillTruck(selectedTruck.id, refillData);
      await loadTrucks();
      await fetchAll(); // Also refresh inventory as station fuel decreased
      setShowRefillModal(false);
      setSelectedTruck(null);
      alert('Truck refilled successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error refilling truck');
    } finally {
      setSaving(false);
    }
  };

  const lowFuelAlerts = inventory.filter(i => i.reorder_level_gallons && i.current_volume_gallons <= i.reorder_level_gallons);
  const lowTruckFuelAlerts = trucks.filter(t => (t.current_volume_gallons / t.capacity_gallons) * 100 < 25);

  // Loading / error states
  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" />
      Loading fuel data…
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <p className="text-red-400">{error}</p>
      <button onClick={fetchAll} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Fuel Management</h2>
          <p className="text-gray-400 text-sm mt-1">Tank inventory, fuel trucks, transactions and deliveries</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={openCreateTank}
            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition">
            <Plus size={16} /> Add Tank
          </button>
          <button onClick={openCreateTruck}
            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition">
            <Truck size={16} /> Add Truck
          </button>
          <button onClick={() => setShowPriceModal(true)}
            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition">
            <DollarSign size={16} /> Update Price
          </button>
          <button onClick={() => setShowDeliveryModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition">
            <RefreshCw size={16} /> Log Delivery
          </button>
          <button onClick={openCreateTx}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition">
            <Plus size={16} /> Fuel Aircraft
          </button>
        </div>
      </div>

      {/* Low fuel alerts */}
      <LowFuelAlert alerts={lowFuelAlerts} />
      
      {lowTruckFuelAlerts.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center gap-3">
          <Truck className="text-orange-400 shrink-0" size={20} />
          <div>
            <p className="text-orange-400 font-medium text-sm">Low fuel in trucks</p>
            <p className="text-orange-300/70 text-xs">
              {lowTruckFuelAlerts.map(t => t.truck_number).join(', ')} — refuel recommended
            </p>
          </div>
        </div>
      )}

      {/* Today stats */}
      <TodayStats stats={todayStats} />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 flex-wrap">
        <button onClick={() => setActiveTab('tanks')}
          className={`px-4 py-2 text-sm font-medium transition ${activeTab === 'tanks' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}>
          Fuel Tanks ({inventory.length})
        </button>
        <button onClick={() => setActiveTab('trucks')}
          className={`px-4 py-2 text-sm font-medium transition ${activeTab === 'trucks' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}>
          Fuel Trucks ({trucks.length})
        </button>
        <button onClick={() => setActiveTab('transactions')}
          className={`px-4 py-2 text-sm font-medium transition ${activeTab === 'transactions' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}>
          Transactions ({transactions.length})
        </button>
      </div>

      {/* Tanks tab */}
      {activeTab === 'tanks' && (
        inventory.length === 0
          ? <p className="text-center text-gray-500 py-16">No fuel tanks found. Click "Add Tank" to create one.</p>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inventory.map(inv => (
                <TankCard key={inv.id} inv={inv} onEdit={openEditTank} onDelete={handleDeleteTank} />
              ))}
            </div>
          )
      )}

      {/* Trucks tab */}
      {activeTab === 'trucks' && (
        trucks.length === 0
          ? (
            <div className="text-center py-16">
              <Truck size={48} className="text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No fuel trucks found.</p>
              <button onClick={openCreateTruck} className="mt-3 text-blue-400 hover:text-blue-300 text-sm">
                + Add your first fuel truck
              </button>
            </div>
          )
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trucks.map(truck => (
                <TruckCard 
                  key={truck.id} 
                  truck={truck} 
                  onEdit={openEditTruck} 
                  onDelete={handleDeleteTruck}
                  onRefill={(t) => {
                    setSelectedTruck(t);
                    setShowRefillModal(true);
                  }}
                  onTransactions={(t) => {
                    setSelectedTruck(t);
                    setShowTruckTxModal(true);
                  }}
                />
              ))}
            </div>
          )
      )}

      {/* Transactions tab */}
      {activeTab === 'transactions' && (
        <TransactionsTable
          transactions={transactions}
          generatingInvoice={generatingInvoice}
          onViewInvoice={handleViewInvoice}
          onDownloadInvoice={handleDownloadInvoice}
          onEdit={openEditTx}
          onDelete={handleDeleteTx}
        />
      )}

      {/* Modals */}
      <TankModal
        isOpen={showTankModal}
        onClose={() => setShowTankModal(false)}
        editingTank={editingTank}
        tankForm={tankForm}
        setTankForm={setTankForm}
        onSave={handleTankSave}
        saving={saving}
      />

      <TransactionModal
        isOpen={showTxModal}
        onClose={closeTxModal}
        editingTx={editingTx}
        txForm={txForm}
        setTxForm={setTxForm}
        inventory={inventory}
        aircraft={aircraft}
        owners={owners}
        staff={staff}
        trucks={trucks}
        onSave={handleTransactionSave}
        saving={saving}
      />

      <DeliveryModal
        isOpen={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        deliveryForm={deliveryForm}
        setDeliveryForm={setDeliveryForm}
        inventory={inventory}
        onSave={handleDelivery}
        saving={saving}
      />

      <PriceModal
        isOpen={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        priceForm={priceForm}
        setPriceForm={setPriceForm}
        inventory={inventory}
        onSave={handlePriceUpdate}
        saving={saving}
      />

      <TruckModal
        isOpen={showTruckModal}
        onClose={() => setShowTruckModal(false)}
        editingTruck={editingTruck}
        truckForm={truckForm}
        setTruckForm={setTruckForm}
        onSave={handleTruckSave}
        saving={saving}
      />

      <TruckRefillModal
        isOpen={showRefillModal}
        onClose={() => {
          setShowRefillModal(false);
          setSelectedTruck(null);
        }}
        truck={selectedTruck}
        onRefill={handleRefillTruck}
        saving={saving}
      />

      <TruckTransactionsModal
        isOpen={showTruckTxModal}
        onClose={() => {
          setShowTruckTxModal(false);
          setSelectedTruck(null);
        }}
        truck={selectedTruck}
      />
    </div>
  );
};

export default FuelView;